import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, Search, Image as ImageIcon } from 'lucide-react';
import { interestsAPI } from '../api';
import { Interest, CreateInterestDto, UpdateInterestDto } from '../types';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/common/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
// Remove alert-dialog import as we'll use Dialog for delete confirmation
import { Badge } from '../components/ui/badge';
import { useNotification } from '../hooks/useNotification';

export const InterestsPage: React.FC = () => {
  const [interests, setInterests] = useState<Interest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState<Interest | null>(null);
  const [formData, setFormData] = useState<CreateInterestDto>({
    name: '',
    image_url: '',
    icon_url: '',
    is_active: true,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedIconFile, setSelectedIconFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [iconPreview, setIconPreview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { showNotification } = useNotification();

  // Fetch interests
  const fetchInterests = async () => {
    try {
      setLoading(true);
      const data = await interestsAPI.getAllInterests();
      console.log('Fetched interests:', data);
      
      // Ensure data is an array
      const interestsArray = Array.isArray(data) ? data : [];
      console.log('First interest with icon_url:', interestsArray.find(i => i.icon_url));
      setInterests(interestsArray);
      setError(null);
    } catch (err) {
      setError('Failed to fetch interests');
      console.error('Error fetching interests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterests();
  }, []);

  // Filter interests based on search
  const filteredInterests = useMemo(() => {
    if (!searchTerm) return interests;
    
    return interests.filter((interest) =>
      interest.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [interests, searchTerm]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle icon file selection
  const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedIconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle create interest
  const handleCreate = async () => {
    try {
      setIsSubmitting(true);
      setFormError(null);

      // Upload image if selected
      let imageUrl = formData.image_url;
      let iconUrl = formData.icon_url;
      
      if (selectedFile) {
        const uploadResult = await interestsAPI.uploadImage(selectedFile);
        imageUrl = uploadResult.url;
      }
      
      if (selectedIconFile) {
        const uploadResult = await interestsAPI.uploadImage(selectedIconFile);
        iconUrl = uploadResult.url;
      }

      await interestsAPI.createInterest({
        ...formData,
        image_url: imageUrl,
        icon_url: iconUrl,
        created_by: 'admin', // This should come from auth context
      });

      showNotification('Interest created successfully', 'success');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchInterests();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string; errors?: Array<{ msg?: string }> } } };
      // Extract error message from response
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.errors?.[0]?.msg ||
                          error.message ||
                          'Failed to create interest';
      setFormError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error creating interest:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit interest
  const handleEdit = async () => {
    if (!selectedInterest) return;

    try {
      setIsSubmitting(true);
      setFormError(null);

      // Upload image if selected
      let imageUrl = formData.image_url;
      let iconUrl = formData.icon_url;
      
      if (selectedFile) {
        const uploadResult = await interestsAPI.uploadImage(selectedFile);
        imageUrl = uploadResult.url;
      }
      
      if (selectedIconFile) {
        const uploadResult = await interestsAPI.uploadImage(selectedIconFile);
        iconUrl = uploadResult.url;
      }

      await interestsAPI.updateInterest(selectedInterest._id, {
        ...formData,
        image_url: imageUrl,
        icon_url: iconUrl,
      } as UpdateInterestDto);

      showNotification('Interest updated successfully', 'success');
      setIsEditDialogOpen(false);
      resetForm();
      fetchInterests();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string; errors?: Array<{ msg?: string }> } } };
      // Extract error message from response
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.errors?.[0]?.msg ||
                          error.message ||
                          'Failed to update interest';
      setFormError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error updating interest:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete interest
  const handleDelete = async () => {
    if (!selectedInterest) return;

    try {
      setIsSubmitting(true);
      setFormError(null);
      await interestsAPI.deleteInterest(selectedInterest._id);
      showNotification('Interest deleted successfully', 'success');
      setIsDeleteDialogOpen(false);
      setSelectedInterest(null);
      fetchInterests();
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string; errors?: Array<{ msg?: string }> } } };
      // Extract error message from response
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.errors?.[0]?.msg ||
                          error.message ||
                          'Failed to delete interest';
      setFormError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error deleting interest:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      image_url: '',
      icon_url: '',
      is_active: true,
    });
    setSelectedFile(null);
    setSelectedIconFile(null);
    setImagePreview('');
    setIconPreview('');
    setSelectedInterest(null);
    setFormError(null);
  };

  // Open edit dialog
  const openEditDialog = (interest: Interest) => {
    setSelectedInterest(interest);
    setFormData({
      name: interest.name,
      image_url: interest.image_url,
      icon_url: interest.icon_url || '',
      is_active: interest.is_active,
    });
    setImagePreview(interest.image_url);
    setIconPreview(interest.icon_url || '');
    setFormError(null);
    setIsEditDialogOpen(true);
  };

  // Open delete dialog
  const openDeleteDialog = (interest: Interest) => {
    setSelectedInterest(interest);
    setFormError(null);
    setIsDeleteDialogOpen(true);
  };

  if (loading) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (error) return <DashboardLayout><ErrorState message={error} onRetry={fetchInterests} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Interests Management"
            description="Manage interests for events and user preferences"
          />

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <CardTitle>All Interests</CardTitle>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="text"
                      placeholder="Search interests..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full sm:w-64"
                    />
                  </div>
                  <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        resetForm();
                        setIsCreateDialogOpen(true);
                      }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Interest
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>
                          <div className="flex items-center gap-2">
                            {iconPreview && (
                              <img
                                src={iconPreview}
                                alt="Interest icon"
                                className="w-6 h-6 object-contain"
                              />
                            )}
                            <span>Create New Interest</span>
                          </div>
                        </DialogTitle>
                        <DialogDescription>
                          Add a new interest with an image and icon
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        {formError && (
                          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                            <p className="text-sm">{formError}</p>
                          </div>
                        )}
                        <div className="grid gap-2">
                          <Label htmlFor="name">Interest Name</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter interest name"
                          />
                          {formData.name && iconPreview && (
                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                              <img
                                src={iconPreview}
                                alt="Icon preview"
                                className="w-5 h-5 object-contain"
                              />
                              <span className="text-sm font-medium">{formData.name}</span>
                            </div>
                          )}
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="image">Interest Image</Label>
                          <Input
                            id="image"
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                          {imagePreview && (
                            <div className="mt-2">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="w-full h-48 object-cover rounded-md"
                              />
                            </div>
                          )}
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="icon">Interest Icon</Label>
                          <Input
                            id="icon"
                            type="file"
                            accept="image/*"
                            onChange={handleIconFileChange}
                          />
                          {iconPreview && (
                            <div className="mt-2">
                              <img
                                src={iconPreview}
                                alt="Icon Preview"
                                className="w-24 h-24 object-contain rounded-md bg-gray-100"
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="h-4 w-4"
                          />
                          <Label htmlFor="is_active">Active</Label>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsCreateDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={handleCreate}
                          disabled={isSubmitting || !formData.name}
                        >
                          {isSubmitting ? 'Creating...' : 'Create'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredInterests.map((interest) => (
                  <div
                    key={interest._id}
                    className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="aspect-square relative bg-gray-100">
                      {interest.image_url ? (
                        <img
                          src={interest.image_url}
                          alt={interest.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                      {interest.icon_url && (
                        <div className="absolute top-2 right-2 w-10 h-10 bg-white rounded-full p-1 shadow-md">
                          <img
                            src={interest.icon_url}
                            alt={`${interest.name} icon`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          {interest.icon_url && (
                            <img
                              src={interest.icon_url}
                              alt={`${interest.name} icon`}
                              className="w-7 h-7 object-contain"
                            />
                          )}
                          <h3 className="font-semibold text-lg">{interest.name}</h3>
                        </div>
                        <Badge variant={interest.is_active ? 'default' : 'secondary'}>
                          {interest.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        Created by: {interest.created_by}
                      </p>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(interest)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteDialog(interest)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredInterests.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">
                    {searchTerm
                      ? 'No interests found matching your search.'
                      : 'No interests found. Create one to get started.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  <div className="flex items-center gap-2">
                    {iconPreview && (
                      <img
                        src={iconPreview}
                        alt="Interest icon"
                        className="w-6 h-6 object-contain"
                      />
                    )}
                    <span>Edit Interest</span>
                  </div>
                </DialogTitle>
                <DialogDescription>
                  Update interest details
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <p className="text-sm">{formError}</p>
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="edit-name">Interest Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter interest name"
                  />
                  {formData.name && iconPreview && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                      <img
                        src={iconPreview}
                        alt="Icon preview"
                        className="w-5 h-5 object-contain"
                      />
                      <span className="text-sm font-medium">{formData.name}</span>
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-image">Interest Image</Label>
                  <Input
                    id="edit-image"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {imagePreview && (
                    <div className="mt-2">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-md"
                      />
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-icon">Interest Icon</Label>
                  <Input
                    id="edit-icon"
                    type="file"
                    accept="image/*"
                    onChange={handleIconFileChange}
                  />
                  {iconPreview && (
                    <div className="mt-2">
                      <img
                        src={iconPreview}
                        alt="Icon Preview"
                        className="w-24 h-24 object-contain rounded-md bg-gray-100"
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="edit-is_active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleEdit}
                  disabled={isSubmitting || !formData.name}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Dialog */}
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  <div className="flex items-center gap-2">
                    {selectedInterest?.icon_url && (
                      <img
                        src={selectedInterest.icon_url}
                        alt="Interest icon"
                        className="w-6 h-6 object-contain"
                      />
                    )}
                    <span>Delete Interest</span>
                  </div>
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete the interest "{selectedInterest?.name}"?
                  This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  <p className="text-sm">{formError}</p>
                </div>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </DashboardLayout>
  );
};